/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { showSuccessThunk } from '@spike/notifications-action';
import { RootState } from 'store';
import { useOrganizationId } from 'hooks/useOrganizationId';
import {
    copyEmailTemplateThunk,
    createOrganizationCampaignThunk,
    deleteEmailTemplateThunk,
    deleteOrganizationCampaignThunk,
    duplicateEmailTemplateThunk,
    duplicateOrganizationCampaignThunk,
    fetchEmailMarketingKpisThunk,
    fetchEmailMarketingLocationsThunk,
    fetchEmailMarketingSendersThunk,
    fetchEmailMarketingTemplatesThunk,
    fetchOrganizationCampaignsThunk,
    refreshEmailMarketingSenderThunk,
    saveEmailMarketingSenderThunk,
    sendOrganizationCampaignThunk,
    updateOrganizationCampaignThunk
} from 'actions/emailMarketing/EmailMarketingActions';
import { OrganizationCampaign } from 'model/emailMarketing';
import { CampaignDraft, EmailCampaign, EmailLocation, EmailSender, EmailTemplate } from './types';
import { EMPTY_DRAFT, draftFromCampaign } from './draft';
import {
    KpiCard,
    toCampaignInput,
    toEmailCampaign,
    toEmailLocation,
    toEmailSender,
    toEmailSenderFromLocation,
    toEmailTemplate,
    toKpiCards
} from './apiAdapters';
import { describeLocations as describeLocationIds } from './utils';

export type EmailMarketingTab = 'campaigns' | 'templates' | 'sender';

const TABS: Array<EmailMarketingTab> = ['campaigns', 'templates', 'sender'];
const SENDING_POLL_MS = 10000;

interface EmailMarketingContextValue {
    tab: EmailMarketingTab;
    setTab: (tab: EmailMarketingTab) => void;

    locations: Array<EmailLocation>;
    // Global location filter: scopes campaigns, templates and senders.
    scopeIds: Array<number>;
    setScopeIds: (ids: Array<number>) => void;
    isAllScope: boolean;

    campaigns: Array<EmailCampaign>;
    // True until the first list of each kind has loaded.
    loadingCampaigns: boolean;
    loadingLocations: boolean;
    loadingTemplates: boolean;
    kpiCards: Array<KpiCard>;
    templates: Array<EmailTemplate>;
    senders: Array<EmailSender>;
    senderFor: (locationId: number) => EmailSender | undefined;
    isSenderReady: (locationId: number) => boolean;
    locationName: (locationId: number | null) => string;
    describeLocations: (locationIds: Array<number>) => string;

    wizardDraft: CampaignDraft | null;
    openNewCampaign: (templateId?: number) => void;
    openDraft: (campaign: EmailCampaign) => void;
    closeWizard: () => void;
    // Resolves true when saved (the wizard closes); false when the API rejected it (the error is shown).
    saveDraft: (draft: CampaignDraft) => Promise<boolean>;
    sendCampaign: (draft: CampaignDraft) => Promise<SendResult>;
    // A save, send, duplicate or delete is in progress.
    saving: boolean;

    duplicateCampaign: (campaign: EmailCampaign) => void;
    deleteCampaign: (campaign: EmailCampaign) => void;
    duplicateTemplate: (template: EmailTemplate, locationId: number) => void;
    copyTemplateToLocations: (template: EmailTemplate, locationIds: Array<number>) => void;
    deleteTemplate: (template: EmailTemplate) => void;
    // Resolves false when the API rejected it (the error is shown); the modal stays open.
    saveSenderEmail: (locationId: number, email: string) => Promise<boolean>;
    resendVerification: (locationId: number) => void;
    // Checks a pending sender's verification status with the email provider.
    refreshSender: (locationId: number) => void;
    notify: (message: string) => void;
}

export interface SendResult {
    sent: boolean;
    // Recipients it was sent to, as the backend resolved them.
    recipients: number;
    // Saved campaign id, also when sending failed, so a retry updates the same draft.
    campaignId: number | null;
}

const EmailMarketingContext = createContext<EmailMarketingContextValue | undefined>(undefined);

export const useEmailMarketing = (): EmailMarketingContextValue => {
    const context = useContext(EmailMarketingContext);
    if (!context) {
        throw new Error('useEmailMarketing must be used within EmailMarketingProvider');
    }
    return context;
};

interface EmailMarketingProviderProps {
    children: React.ReactNode;
}

// Organization email marketing state: data from the Organization Portal API (store/emailMarketing),
// mapped to the screens' view models (./apiAdapters), plus the UI state shared by the tabs and the wizard.
export const EmailMarketingProvider: React.FC<EmailMarketingProviderProps> = ({ children }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const organizationId = useOrganizationId();
    const api = useSelector((state: RootState) => state.emailMarketing);

    const tabParam = params.get('tab') as EmailMarketingTab;
    const tab: EmailMarketingTab = TABS.includes(tabParam) ? tabParam : 'campaigns';

    // null = every location, so the scope stays "all" when the locations arrive.
    const [scopeState, setScopeState] = useState<Array<number> | null>(null);
    const [wizardDraft, setWizardDraft] = useState<CampaignDraft | null>(null);

    const locations = useMemo(() => api.locations.map(toEmailLocation), [api.locations]);
    const allLocationIds = useMemo(() => locations.map(item => item.id), [locations]);
    const scopeIds = scopeState ?? allLocationIds;
    const isAllScope = scopeState === null || scopeState.length === allLocationIds.length;

    const campaigns = useMemo(() => api.campaigns.map(toEmailCampaign), [api.campaigns]);
    const templates = useMemo(() => api.templates.map(toEmailTemplate), [api.templates]);
    // Until the senders load, the location picker data says which locations can send.
    const senders = useMemo(
        () => (api.senders.length > 0 ? api.senders.map(toEmailSender) : api.locations.map(toEmailSenderFromLocation)),
        [api.senders, api.locations]
    );

    // "Campaigns sent" is counted from the list, so it uses the same location filter as the API KPIs.
    const kpiCards = useMemo(() => {
        const scoped = isAllScope
            ? campaigns
            : campaigns.filter(campaign => campaign.locationIds.some(id => scopeIds.includes(id)));
        return toKpiCards(api.kpis, scoped);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api.kpis, campaigns, isAllScope, scopeIds.join(',')]);

    useEffect(() => {
        if (!organizationId) return;
        dispatch(fetchEmailMarketingLocationsThunk(organizationId) as any);
        dispatch(fetchEmailMarketingTemplatesThunk(organizationId) as any);
        dispatch(fetchEmailMarketingSendersThunk(organizationId) as any);
        dispatch(fetchOrganizationCampaignsThunk(organizationId) as any);
    }, [dispatch, organizationId]);

    // Bumped after a send and while campaigns are sending, so KPIs and statuses don't need a reload.
    const [refreshKey, setRefreshKey] = useState(0);
    const hasSending = api.campaigns.some(campaign => campaign.status === 'sending');
    useEffect(() => {
        if (!hasSending || !organizationId) return;
        const timer = setInterval(() => {
            dispatch(fetchOrganizationCampaignsThunk(organizationId) as any);
            setRefreshKey(prev => prev + 1);
        }, SENDING_POLL_MS);
        return () => clearInterval(timer);
    }, [dispatch, hasSending, organizationId]);

    // KPIs follow the location filter; no locations sent means every location.
    const kpiLocationIds = isAllScope ? undefined : scopeIds;
    useEffect(() => {
        if (!organizationId) return;
        dispatch(fetchEmailMarketingKpisThunk(organizationId, { locationIds: kpiLocationIds }) as any);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, organizationId, kpiLocationIds?.join(','), refreshKey]);

    const notify = useCallback((message: string) => dispatch(showSuccessThunk(message) as any), [dispatch]);

    const setTab = useCallback(
        (next: EmailMarketingTab) => {
            const nextParams = new URLSearchParams(location.search);
            nextParams.set('tab', next);
            history.replace({ search: nextParams.toString() });
        },
        [history, location.search]
    );

    const senderFor = useCallback((locationId: number) => senders.find(s => s.locationId === locationId), [senders]);
    const isSenderReady = useCallback(
        (locationId: number) => senderFor(locationId)?.status === 'verified',
        [senderFor]
    );

    // Runs a request, shows `message` when it succeeds; errors are already shown by the thunk.
    const run = (request: any, message: string) =>
        (dispatch(request) as unknown as Promise<unknown>).then(() => notify(message)).catch(() => undefined);

    // Creates the draft or saves the changes; the API keeps it as a draft until it is sent.
    const persistDraft = (draft: CampaignDraft): Promise<OrganizationCampaign> => {
        const input = toCampaignInput(draft);
        const request = draft.id
            ? updateOrganizationCampaignThunk(organizationId as number, draft.id, input)
            : createOrganizationCampaignThunk(organizationId as number, input);
        return dispatch(request as any) as unknown as Promise<OrganizationCampaign>;
    };

    const value: EmailMarketingContextValue = {
        tab,
        setTab,
        locations,
        scopeIds,
        setScopeIds: ids => setScopeState(ids.length && ids.length < allLocationIds.length ? ids : null),
        isAllScope,
        campaigns,
        loadingCampaigns: !api.campaignsLoaded,
        loadingLocations: !api.locationsLoaded,
        loadingTemplates: !api.templatesLoaded,
        kpiCards,
        templates,
        senders,
        senderFor,
        isSenderReady,
        locationName: locationId => locations.find(item => item.id === locationId)?.name ?? '',
        describeLocations: locationIds => describeLocationIds(locationIds, locations),
        wizardDraft,
        openNewCampaign: templateId => {
            const template = templates.find(item => item.id === templateId);
            setWizardDraft({
                ...EMPTY_DRAFT,
                // Pre-select the locations in scope that can actually send.
                locationIds: scopeIds.filter(isSenderReady),
                templateId: template?.id ?? null,
                subject: template?.subjectDefault ?? ''
            });
        },
        openDraft: campaign => setWizardDraft(draftFromCampaign(campaign)),
        closeWizard: () => setWizardDraft(null),
        saveDraft: async draft => {
            if (!organizationId) return false;
            try {
                await persistDraft(draft);
            } catch {
                return false;
            }
            setWizardDraft(null);
            notify('Draft saved');
            return true;
        },
        sendCampaign: async draft => {
            if (!organizationId) return { sent: false, recipients: 0, campaignId: draft.id };
            let campaignId = draft.id;
            try {
                // The draft is saved first so the API sends exactly what the wizard shows.
                campaignId = (await persistDraft(draft)).id;
                const sent = (await dispatch(
                    sendOrganizationCampaignThunk(organizationId, campaignId) as any
                )) as unknown as OrganizationCampaign;
                setRefreshKey(prev => prev + 1);
                return { sent: true, recipients: sent.totals?.recipients ?? 0, campaignId };
            } catch {
                return { sent: false, recipients: 0, campaignId };
            }
        },
        saving: api.saving,
        duplicateCampaign: campaign => {
            if (!organizationId) return;
            run(duplicateOrganizationCampaignThunk(organizationId, campaign.id), 'Campaign duplicated as a draft');
        },
        deleteCampaign: campaign => {
            if (!organizationId) return;
            run(deleteOrganizationCampaignThunk(organizationId, campaign.id), 'Campaign deleted');
        },
        duplicateTemplate: (template, locationId) => {
            if (!organizationId) return;
            // A gallery template is added to the location as a copy; a location template is duplicated in place.
            run(
                template.source === 'gallery'
                    ? copyEmailTemplateThunk(organizationId, template.id, [locationId])
                    : duplicateEmailTemplateThunk(organizationId, template.id),
                'Template added'
            );
        },
        copyTemplateToLocations: (template, locationIds) => {
            if (!organizationId) return;
            run(
                copyEmailTemplateThunk(organizationId, template.id, locationIds),
                `Template copied to ${locationIds.length} location${locationIds.length === 1 ? '' : 's'}`
            );
        },
        deleteTemplate: template => {
            if (!organizationId) return;
            run(deleteEmailTemplateThunk(organizationId, template.id), 'Template deleted');
        },
        saveSenderEmail: async (locationId, email) => {
            if (!organizationId) return false;
            try {
                await (dispatch(
                    saveEmailMarketingSenderThunk(organizationId, locationId, email) as any
                ) as unknown as Promise<unknown>);
            } catch {
                return false;
            }
            notify('Verification email sent');
            return true;
        },
        resendVerification: locationId => {
            const email = senderFor(locationId)?.email;
            if (!organizationId || !email) return;
            // Saving the same unverified email asks the provider to send the link again.
            run(saveEmailMarketingSenderThunk(organizationId, locationId, email), 'Verification email sent again');
        },
        refreshSender: locationId => {
            if (!organizationId) return;
            (
                dispatch(
                    refreshEmailMarketingSenderThunk(organizationId, locationId) as any
                ) as unknown as Promise<unknown>
            ).catch(() => undefined);
        },
        notify
    };

    return <EmailMarketingContext.Provider value={value}>{children}</EmailMarketingContext.Provider>;
};

export default EmailMarketingProvider;
